import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ApiResponse,
  GetMembershipPlansParams,
  GetOperationalMembershipPlansParams,
  GetVisibleMembershipPlansParams,
  MembershipPlan,
  MembershipPlanDTO,
} from "@membentry/shared-types";
import type { AxiosInstance } from "axios";
import { getNextPageFromMeta } from "../utils/infinite-pagination";

export const MEMBERSHIP_PLANS_QUERY_KEY = "membership-plans";

export function useGetMembershipPlansQuery(
  client: AxiosInstance,
  params: GetMembershipPlansParams = {},
  options?: Omit<
    UseQueryOptions<ApiResponse<MembershipPlanDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { page = 0, pageSize = 10, search = "" } = params;

  return useQuery({
    queryKey: [MEMBERSHIP_PLANS_QUERY_KEY, page, pageSize, search],
    queryFn: async () => {
      const { getMembershipPlans } = await import("@membentry/shared-api");
      return getMembershipPlans(client, { page, pageSize, search });
    },
    ...options,
  });
}

export function useGetVisibleMembershipPlansQuery(
  client: AxiosInstance,
  clubId: number,
  params: GetVisibleMembershipPlansParams = {},
  options?: Omit<
    UseQueryOptions<ApiResponse<MembershipPlanDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { page = 0, pageSize = 10, search = "" } = params;

  return useQuery({
    queryKey: [MEMBERSHIP_PLANS_QUERY_KEY, "visible", clubId, page, pageSize, search],
    queryFn: async () => {
      const { getVisibleMembershipPlans } = await import("@membentry/shared-api");
      return getVisibleMembershipPlans(client, clubId, { page, pageSize, search });
    },
    enabled: clubId > 0,
    ...options,
  });
}

export function useGetOperationalMembershipPlansQuery(
  client: AxiosInstance,
  clubId: number,
  params: GetOperationalMembershipPlansParams = {},
  options?: Omit<
    UseQueryOptions<ApiResponse<MembershipPlanDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { page = 0, pageSize = 10, search = "" } = params;

  return useQuery({
    queryKey: [MEMBERSHIP_PLANS_QUERY_KEY, "operational", clubId, page, pageSize, search],
    queryFn: async () => {
      const { getOperationalMembershipPlans } = await import("@membentry/shared-api");
      return getOperationalMembershipPlans(client, clubId, { page, pageSize, search });
    },
    enabled: clubId > 0,
    ...options,
  });
}

export function useGetMembershipPlansInfiniteQuery(
  client: AxiosInstance,
  params: Omit<GetMembershipPlansParams, "page"> = {},
  options?: Omit<
    UseInfiniteQueryOptions<
      ApiResponse<MembershipPlanDTO[]>,
      Error,
      InfiniteData<ApiResponse<MembershipPlanDTO[]>>,
      readonly unknown[],
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >,
) {
  const { pageSize = 10, search = "" } = params;

  return useInfiniteQuery<
    ApiResponse<MembershipPlanDTO[]>,
    Error,
    InfiniteData<ApiResponse<MembershipPlanDTO[]>>,
    readonly unknown[],
    number
  >({
    queryKey: [MEMBERSHIP_PLANS_QUERY_KEY, "infinite", pageSize, search],
    queryFn: async ({ pageParam = 0 }) => {
      const { getMembershipPlans } = await import("@membentry/shared-api");
      return getMembershipPlans(client, {
        page: pageParam as number,
        pageSize,
        search,
      });
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageFromMeta(lastPage, allPages.length, pageSize),
    initialPageParam: 0,
    ...options,
  });
}

export function useAddMembershipPlanMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<MembershipPlanDTO>, Error, MembershipPlan>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (plan: MembershipPlan) => {
      const { addMembershipPlan } = await import("@membentry/shared-api");
      return addMembershipPlan(client, plan);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_PLANS_QUERY_KEY] });
    },
    ...options,
  });
}

export function useUpdateMembershipPlanMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<MembershipPlanDTO>,
      Error,
      { id: number; plan: MembershipPlanDTO }
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      plan,
    }: {
      id: number;
      plan: MembershipPlanDTO;
    }) => {
      const { updateMembershipPlan } = await import("@membentry/shared-api");
      return updateMembershipPlan(client, id, plan);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_PLANS_QUERY_KEY] });
    },
    ...options,
  });
}

export function useDeleteMembershipPlanMutation(
  client: AxiosInstance,
  options?: Omit<UseMutationOptions<void, Error, number>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { deleteMembershipPlan } = await import("@membentry/shared-api");
      return deleteMembershipPlan(client, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_PLANS_QUERY_KEY] });
    },
    ...options,
  });
}

export function invalidateMembershipPlans(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_PLANS_QUERY_KEY] });
}
